/**
 * Design Configurator - Main Orchestrator
 * Initializes and coordinates all configurator components
 */

import { ConfiguratorState } from "@configurator/state";
import { DOMManager } from "@configurator/dom";
import { TabManager } from "@configurator/tabs";
import { DesignManager } from "@configurator/design";
import { PreviewManager } from "@configurator/preview";
import { CartManager } from "@configurator/cart";
import { PalettesManager } from "@configurator/palettes";
import { OptionsManager } from "@configurator/options";
import { URLStateManager } from "@configurator/url";
import { ProductPickerManager } from "@configurator/product-picker";
import { t } from "@configurator/i18n";

const MODAL_TRANSITION_MS = 320;
const MOBILE_CONTROLS_MIN_RATIO = 0.5;
const PREVIEW_ACTIONS_SAFE_GAP_PX = 6;

class DesignConfigurator {
  constructor() {
    // Initialize core components
    this.startup = window.configuratorStartup;
    this.startup.claimed = true;
    this.startup.requestedURL = this.startup.entryURL;
    this.generation = 0;
    this.state = new ConfiguratorState();
    this.state.loadConfigData();
    this.urlManager = new URLStateManager(this.state);
    this.dom = new DOMManager();

    // Initialize feature managers
    this.tabManager = new TabManager(this.state, this.dom);
    this.designManager = new DesignManager(this.state, this.dom);
    this.previewManager = new PreviewManager(this.state, this.dom);
    this.optionsManager = new OptionsManager(this.state, this.dom);
    this.cartManager = new CartManager(this.state, this.dom);
    this.palettesManager = new PalettesManager(
      this.state,
      this.dom,
      this.designManager
    );
    this.productPickerManager = new ProductPickerManager(this.dom);

    this.setupControlsToggle();
    this.setupFullscreenToggle();
    this.setupMobilePanelSizing();
    this.setupExitConfirmation();
    this.setupURLSync();
    this.welcomeHelper = this.setupWelcomeHelper();
    this.setupProductChangeActions();
    this.setupPostCartOfferFlow();
  }

  setupWelcomeHelper() {
    const {
      welcomeModal,
      welcomeStartButton,
      welcomeCloseButton,
      welcomeDontShowCheckbox,
      welcomeLoginLink,
      welcomeOverlay,
      welcomeInfoButton,
      configuratorRoot,
    } = this.dom.elements;

    if (!welcomeModal || !welcomeStartButton) {
      return {
        shouldAutoShow: false,
        open: () => {},
        close: () => {},
      };
    }

    const storageKey = "design-configurator:welcome-dismissed";
    let hideModalTimeout = null;
    let openModalFrame = null;
    const localStorageAvailable = (() => {
      try {
        const testKey = "__configurator_test__";
        window.localStorage.setItem(testKey, "1");
        window.localStorage.removeItem(testKey);
        return true;
      } catch (error) {
        console.warn("LocalStorage not available for welcome modal", error);
        return false;
      }
    })();

    const shouldSkipAutoShow =
      localStorageAvailable &&
      window.localStorage.getItem(storageKey) === "true";

    const isCustomerLoggedIn =
      Boolean(window.configuratorData?.customer?.id) || false;

    if (welcomeLoginLink) {
      if (isCustomerLoggedIn) {
        welcomeLoginLink.setAttribute("hidden", "hidden");
      } else {
        welcomeLoginLink.removeAttribute("hidden");
        const loginBase = welcomeLoginLink.dataset.loginBase;
        if (loginBase) {
          try {
            const loginURL = new URL(loginBase, window.location.origin);
            const returnParam =
              welcomeLoginLink.dataset.loginReturnParam || "return_to";
            const localeParam = welcomeLoginLink.dataset.loginLocaleParam;
            const localeValue = welcomeLoginLink.dataset.loginLocaleValue;
            const currentPath =
              window.location.pathname + window.location.search;

            if (localeParam && localeValue) {
              loginURL.searchParams.set(localeParam, localeValue);
            }

            if (currentPath) {
              loginURL.searchParams.set(returnParam, currentPath);
            }

            welcomeLoginLink.href = loginURL.toString();
          } catch (urlError) {
            console.warn("Unable to construct login URL", urlError);
          }
        }
      }
    }

    const showModal = () => {
      if (hideModalTimeout) {
        window.clearTimeout(hideModalTimeout);
        hideModalTimeout = null;
      }
      if (openModalFrame) {
        window.cancelAnimationFrame(openModalFrame);
        openModalFrame = null;
      }

      welcomeModal.hidden = false;
      welcomeModal.classList.remove("is-visible");
      configuratorRoot?.classList.add("design-configurator--modal-open");
      document.body?.classList?.add("configurator-helper-modal-open");
      void welcomeModal.offsetWidth;

      openModalFrame = window.requestAnimationFrame(() => {
        openModalFrame = window.requestAnimationFrame(() => {
          welcomeModal.classList.add("is-visible");
          openModalFrame = null;
        });
      });
    };

    const hideModal = () => {
      if (openModalFrame) {
        window.cancelAnimationFrame(openModalFrame);
        openModalFrame = null;
      }

      welcomeModal.classList.remove("is-visible");

      const finalizeClose = () => {
        if (welcomeModal.classList.contains("is-visible")) {
          return;
        }

        welcomeModal.hidden = true;
        configuratorRoot?.classList.remove("design-configurator--modal-open");
        document.body?.classList?.remove("configurator-helper-modal-open");
        hideModalTimeout = null;
      };

      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (prefersReducedMotion) {
        finalizeClose();
        return;
      }

      hideModalTimeout = window.setTimeout(finalizeClose, MODAL_TRANSITION_MS);
    };

    const persistPreference = () => {
      if (!localStorageAvailable) return;
      if (welcomeDontShowCheckbox?.checked) {
        window.localStorage.setItem(storageKey, "true");
      }
    };

    const closeModal = () => {
      persistPreference();
      hideModal();
    };

    const openModal = ({ resetDismissChoice = false, force = false } = {}) => {
      if (!force && shouldSkipAutoShow) {
        return;
      }
      if (resetDismissChoice && welcomeDontShowCheckbox) {
        welcomeDontShowCheckbox.checked = false;
      }
      showModal();
    };

    welcomeStartButton.addEventListener("click", closeModal);
    welcomeCloseButton?.addEventListener("click", closeModal);

    welcomeOverlay?.addEventListener("click", (event) => {
      event.preventDefault();
      closeModal();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !welcomeModal.hidden) {
        closeModal();
      }
    });

    welcomeInfoButton?.addEventListener("click", (event) => {
      event.preventDefault();
      openModal({ resetDismissChoice: true, force: true });
    });

    return {
      shouldAutoShow: !shouldSkipAutoShow,
      open: openModal,
      close: closeModal,
    };
  }

  setupURLSync() {
    // Subscribe to state changes to update URL
    this.state.subscribe((change) => {
      if (change.type === "design" || change.type === "options:selection") {
        this.urlManager.updateURL();
      }
    });

    // Setup browser history navigation
    this.urlManager.setupHistoryNavigation();
  }

  setupControlsToggle() {
    const toggleButton = this.dom.elements.controlsToggle;
    const configuratorRoot = this.dom.elements.configuratorRoot;
    const controlsPanel = this.dom.elements.controlsPanel;
    const rootCollapsedClass = "design-configurator--controls-collapsed";
    const panelCollapsedClass = "configurator-controls--collapsed";

    if (!toggleButton || !configuratorRoot || !controlsPanel) {
      return;
    }

    const toggleIcon = toggleButton.querySelector(
      ".configurator-controls__toggle-icon"
    );
    const toggleLabel = toggleButton.querySelector(
      ".configurator-controls__toggle-label"
    );
    const mobileQuery =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(max-width: 767px)")
        : null;

    let isCollapsed =
      controlsPanel.classList.contains(panelCollapsedClass) ||
      configuratorRoot.classList.contains(rootCollapsedClass);

    const getToggleDirection = () => {
      const isMobile = mobileQuery ? mobileQuery.matches : false;
      if (isMobile) {
        return isCollapsed ? "up" : "down";
      }
      return isCollapsed ? "left" : "right";
    };

    const updateToggleUI = () => {
      controlsPanel.classList.toggle(panelCollapsedClass, isCollapsed);
      configuratorRoot.classList.toggle(rootCollapsedClass, isCollapsed);
      toggleButton.setAttribute("aria-expanded", (!isCollapsed).toString());

      if (toggleIcon) {
        toggleIcon.dataset.direction = getToggleDirection();
      }

      if (toggleLabel) {
        toggleLabel.textContent = isCollapsed
          ? t("controls.show_panel", {}, "Afficher le panneau de configuration")
          : t("controls.hide_panel", {}, "Masquer le panneau de configuration");
      }
    };

    const handleBreakpointChange = () => updateToggleUI();

    toggleButton.addEventListener("click", () => {
      isCollapsed = !isCollapsed;
      updateToggleUI();
    });

    if (mobileQuery) {
      if (typeof mobileQuery.addEventListener === "function") {
        mobileQuery.addEventListener("change", handleBreakpointChange);
      } else if (typeof mobileQuery.addListener === "function") {
        mobileQuery.addListener(handleBreakpointChange);
      }
    }

    updateToggleUI();
  }

  setupFullscreenToggle() {
    const configuratorRoot = this.dom.elements.configuratorRoot;
    const fullscreenButton = this.dom.elements.fullscreenButton;
    const fullscreenButtonLabel = this.dom.elements.fullscreenButtonLabel;
    const pageFullscreenClass = "configurator-page-fullscreen";
    const rootFullscreenClass = "design-configurator--fullscreen-active";
    const documentElement = document.documentElement;
    const bodyElement = document.body;
    const headerGroup = document.getElementById("header-group");

    if (!configuratorRoot || !fullscreenButton) {
      return;
    }

    const requestNativeFullscreen =
      configuratorRoot.requestFullscreen?.bind(configuratorRoot) ||
      configuratorRoot.webkitRequestFullscreen?.bind(configuratorRoot);
    const exitNativeFullscreen =
      document.exitFullscreen?.bind(document) ||
      document.webkitExitFullscreen?.bind(document);
    const fullscreenChangeEvents = [
      "fullscreenchange",
      "webkitfullscreenchange",
    ];
    const isIPhone = /iPhone|iPod/i.test(navigator.userAgent || "");
    const nativeFullscreenEnabled =
      document.fullscreenEnabled ?? document.webkitFullscreenEnabled ?? true;
    const supportsNativeFullscreen =
      !isIPhone &&
      nativeFullscreenEnabled !== false &&
      typeof requestNativeFullscreen === "function" &&
      typeof exitNativeFullscreen === "function";

    let isPageFullscreen =
      documentElement?.classList?.contains(pageFullscreenClass) ||
      bodyElement?.classList?.contains(pageFullscreenClass) ||
      false;
    let isTransitioning = false;
    let isUsingNativeFullscreen = false;

    const getEnterLabel = () =>
      fullscreenButton.dataset.enterLabel || "Open image in full screen";
    const getExitLabel = () =>
      fullscreenButton.dataset.exitLabel || "Exit full screen";
    const getNativeFullscreenElement = () =>
      document.fullscreenElement || document.webkitFullscreenElement || null;
    const isNativeFullscreenActive = () =>
      getNativeFullscreenElement() === configuratorRoot;
    const setHeaderVisibility = (isVisible) => {
      if (!headerGroup) {
        return;
      }

      headerGroup.hidden = !isVisible;
      headerGroup.style.display = isVisible ? "" : "none";

      if (isVisible) {
        headerGroup.removeAttribute("aria-hidden");
      } else {
        headerGroup.setAttribute("aria-hidden", "true");
      }
    };

    const setPageFullscreenState = (isActive) => {
      isPageFullscreen = isActive;
      documentElement?.classList?.toggle(pageFullscreenClass, isActive);
      bodyElement?.classList?.toggle(pageFullscreenClass, isActive);

      if (isActive) {
        bodyElement?.style?.setProperty("--header-group-height", "0px");
      } else {
        bodyElement?.style?.removeProperty("--header-group-height");
      }

      setHeaderVisibility(!isActive);
    };

    const updateButtonState = () => {
      const isActive = isPageFullscreen || isNativeFullscreenActive();
      const label = isActive ? getExitLabel() : getEnterLabel();

      fullscreenButton.dataset.fullscreenActive = isActive.toString();
      fullscreenButton.setAttribute("aria-pressed", isActive.toString());
      fullscreenButton.title = label;
      configuratorRoot.classList.toggle(rootFullscreenClass, isActive);

      if (fullscreenButtonLabel) {
        fullscreenButtonLabel.textContent = label;
      }
    };

    const enterFullscreen = async () => {
      setPageFullscreenState(true);
      updateButtonState();

      if (!supportsNativeFullscreen || isNativeFullscreenActive()) {
        return;
      }

      try {
        await requestNativeFullscreen();
        isUsingNativeFullscreen = isNativeFullscreenActive();
      } catch (error) {
        console.warn(
          "Unable to enter native fullscreen, keeping page fullscreen only",
          error
        );
        isUsingNativeFullscreen = false;
        updateButtonState();
      }
    };

    const exitFullscreen = async () => {
      if (supportsNativeFullscreen && getNativeFullscreenElement()) {
        try {
          await exitNativeFullscreen();
        } catch (error) {
          console.warn("Unable to exit native fullscreen", error);
        }
      }

      isUsingNativeFullscreen = false;
      setPageFullscreenState(false);
      updateButtonState();
    };

    fullscreenButton.addEventListener("click", async () => {
      if (isTransitioning) {
        return;
      }

      isTransitioning = true;

      try {
        if (isPageFullscreen || isNativeFullscreenActive()) {
          await exitFullscreen();
        } else {
          await enterFullscreen();
        }
      } finally {
        isTransitioning = false;
      }
    });

    const syncFullscreenState = () => {
      if (isNativeFullscreenActive()) {
        isUsingNativeFullscreen = true;
        setPageFullscreenState(true);
      } else if (isUsingNativeFullscreen) {
        isUsingNativeFullscreen = false;
        setPageFullscreenState(false);
      }

      updateButtonState();
    };

    fullscreenChangeEvents.forEach((eventName) => {
      document.addEventListener(eventName, syncFullscreenState);
    });

    setPageFullscreenState(isPageFullscreen);
    updateButtonState();
  }

  setupMobilePanelSizing() {
    const {
      configuratorRoot,
      configuratorContainer,
      controlsPanel,
      previewWrapper,
      previewContainer,
      preview,
      overlay,
      baseImage,
      previewLeftActions,
      previewTopActions,
      previewActions,
    } = this.dom.elements;
    const mobileQuery =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(max-width: 767px)")
        : null;

    if (
      !mobileQuery ||
      !configuratorRoot ||
      !configuratorContainer ||
      !controlsPanel ||
      !previewWrapper ||
      !previewContainer ||
      !preview
    ) {
      return;
    }

    let frameId = 0;

    const syncLayout = () => {
      frameId = 0;
      this.syncMobilePanelSizing({
        mobileQuery,
        configuratorRoot,
        configuratorContainer,
        controlsPanel,
        previewWrapper,
        previewContainer,
        preview,
        overlay,
        baseImage,
        previewLeftActions,
        previewTopActions,
        previewActions,
      });
      this.syncPreviewSafeArea({
        previewContainer,
        previewLeftActions,
        previewTopActions,
        previewActions,
      });
    };

    const scheduleSync = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(syncLayout);
    };

    const resizeTargets = [
      configuratorRoot,
      configuratorContainer,
      controlsPanel,
      previewWrapper,
      previewContainer,
      preview,
      baseImage,
    ].filter(Boolean);

    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(() => {
        scheduleSync();
      });

      resizeTargets.forEach((target) => resizeObserver.observe(target));
    }

    if (overlay && typeof MutationObserver === "function") {
      const overlayObserver = new MutationObserver(() => {
        scheduleSync();
      });

      overlayObserver.observe(overlay, {
        childList: true,
        subtree: true,
      });
    }

    if (baseImage) {
      baseImage.addEventListener("load", scheduleSync);
      baseImage.addEventListener("error", scheduleSync);
    }

    const breakpointListener = () => {
      scheduleSync();
    };

    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", breakpointListener);
    } else if (typeof mobileQuery.addListener === "function") {
      mobileQuery.addListener(breakpointListener);
    }

    window.addEventListener("resize", scheduleSync);
    window.addEventListener("orientationchange", scheduleSync);
    window.addEventListener("load", scheduleSync);
    window.addEventListener("configurator:product-updated", scheduleSync);
    document.addEventListener("fullscreenchange", scheduleSync);
    document.addEventListener("webkitfullscreenchange", scheduleSync);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", scheduleSync);
    }

    scheduleSync();
  }

  syncMobilePanelSizing({
    mobileQuery,
    configuratorRoot,
    configuratorContainer,
    controlsPanel,
    previewWrapper,
    preview,
    overlay,
    baseImage,
    previewLeftActions,
    previewTopActions,
    previewActions,
  }) {
    if (!mobileQuery?.matches) {
      configuratorRoot.style.removeProperty(
        "--configurator-mobile-controls-max-height"
      );
      return;
    }

    const containerRect = configuratorContainer.getBoundingClientRect();
    const previewRect = preview.getBoundingClientRect();

    if (containerRect.height <= 0 || previewRect.width <= 0) {
      return;
    }

    const availableHeight = Math.round(containerRect.height);
    const previewGraphicHeight = this.getMobilePreviewGraphicHeight({
      preview,
      overlay,
      baseImage,
    });
    const previewChromeHeight = this.getMobilePreviewChromeHeight({
      previewWrapper,
      previewLeftActions,
      previewTopActions,
      previewActions,
    });
    const previewRequiredHeight = Math.round(
      previewGraphicHeight +
        previewChromeHeight +
        PREVIEW_ACTIONS_SAFE_GAP_PX * 2
    );
    const minimumPanelHeight = Math.round(
      availableHeight * MOBILE_CONTROLS_MIN_RATIO
    );
    const dynamicPanelHeight = Math.max(
      availableHeight - previewRequiredHeight,
      0
    );
    const nextMaxHeight = Math.max(minimumPanelHeight, dynamicPanelHeight);

    configuratorRoot.style.setProperty(
      "--configurator-mobile-controls-max-height",
      `${nextMaxHeight}px`
    );
  }

  syncPreviewSafeArea({
    previewContainer,
    previewLeftActions,
    previewTopActions,
    previewActions,
  }) {
    if (!previewContainer) {
      return;
    }

    const previewRect = previewContainer.getBoundingClientRect();

    if (previewRect.height <= 0 || previewRect.width <= 0) {
      previewContainer.style.removeProperty("--configurator-preview-safe-top");
      previewContainer.style.removeProperty(
        "--configurator-preview-safe-bottom"
      );
      return;
    }

    const topElements = [previewLeftActions, previewTopActions];
    const topExtent = topElements.reduce((maxExtent, element) => {
      if (!element || element.hidden) {
        return maxExtent;
      }

      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return maxExtent;
      }

      return Math.max(maxExtent, rect.bottom - previewRect.top);
    }, 0);

    let bottomExtent = 0;
    if (previewActions && !previewActions.hidden) {
      const rect = previewActions.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        bottomExtent = Math.max(previewRect.bottom - rect.top, 0);
      }
    }

    const safeTop = topExtent
      ? Math.ceil(topExtent + PREVIEW_ACTIONS_SAFE_GAP_PX)
      : 0;
    const safeBottom = bottomExtent
      ? Math.ceil(bottomExtent + PREVIEW_ACTIONS_SAFE_GAP_PX)
      : 0;

    previewContainer.style.setProperty(
      "--configurator-preview-safe-top",
      `${safeTop}px`
    );
    previewContainer.style.setProperty(
      "--configurator-preview-safe-bottom",
      `${safeBottom}px`
    );
  }

  getMobilePreviewGraphicHeight({ preview, overlay, baseImage }) {
    const previewWidth = preview?.getBoundingClientRect?.().width || 0;

    if (previewWidth <= 0) {
      return 0;
    }

    const imageHeight = this.getMobileBaseImageHeight(previewWidth, baseImage);
    const svgHeight = this.getMobileSvgGraphicHeight(previewWidth, overlay);

    return Math.round(Math.max(imageHeight, svgHeight, 0));
  }

  getMobileBaseImageHeight(previewWidth, baseImage) {
    if (!baseImage || baseImage.hidden || previewWidth <= 0) {
      return 0;
    }

    const naturalWidth =
      baseImage.naturalWidth ||
      Number.parseFloat(baseImage.getAttribute("width")) ||
      0;
    const naturalHeight =
      baseImage.naturalHeight ||
      Number.parseFloat(baseImage.getAttribute("height")) ||
      0;

    if (naturalWidth > 0 && naturalHeight > 0) {
      return (previewWidth * naturalHeight) / naturalWidth;
    }

    return baseImage.getBoundingClientRect().height || 0;
  }

  getMobileSvgGraphicHeight(previewWidth, overlay) {
    const svgElement = overlay?.querySelector?.("svg");

    if (!svgElement || previewWidth <= 0) {
      return 0;
    }

    let viewBoxWidth = 0;
    let contentHeight = 0;
    const viewBox = svgElement.viewBox?.baseVal;

    if (viewBox?.width > 0 && viewBox?.height > 0) {
      viewBoxWidth = viewBox.width;
      contentHeight = viewBox.height;
    } else {
      const widthAttr = Number.parseFloat(svgElement.getAttribute("width")) || 0;
      const heightAttr =
        Number.parseFloat(svgElement.getAttribute("height")) || 0;

      if (widthAttr > 0 && heightAttr > 0) {
        viewBoxWidth = widthAttr;
        contentHeight = heightAttr;
      }
    }

    // getBBox ignore les clip-path : les copies de tuile qui dépassent des zones motif
    // (#motif-*, découpées) agrandiraient la hauteur selon le motif. On les masque le temps
    // de la mesure, pour ne mesurer que la sacoche.
    const motifZones = [...svgElement.querySelectorAll('[id^="motif-"]')];
    const displays = motifZones.map((zone) => zone.style.display);
    motifZones.forEach((zone) => {
      zone.style.display = "none";
    });

    try {
      const bbox = svgElement.getBBox();
      if (bbox?.width > 0 && bbox?.height > 0) {
        viewBoxWidth = viewBoxWidth || bbox.width;
        contentHeight = bbox.height;
      }
    } catch {
    } finally {
      motifZones.forEach((zone, index) => {
        zone.style.display = displays[index];
      });
    }

    if (viewBoxWidth <= 0 || contentHeight <= 0) {
      return 0;
    }

    return (previewWidth * contentHeight) / viewBoxWidth;
  }

  getMobilePreviewChromeHeight({
    previewWrapper,
    previewLeftActions,
    previewTopActions,
    previewActions,
  }) {
    const previewRect = previewWrapper?.getBoundingClientRect?.();

    if (!previewRect || previewRect.height <= 0) {
      return 0;
    }

    const topElements = [previewLeftActions, previewTopActions];
    const topExtent = topElements.reduce((maxExtent, element) => {
      if (!element || element.hidden) {
        return maxExtent;
      }

      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return maxExtent;
      }

      return Math.max(maxExtent, rect.bottom - previewRect.top);
    }, 0);

    let bottomExtent = 0;
    if (previewActions && !previewActions.hidden) {
      const rect = previewActions.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        bottomExtent = Math.max(previewRect.bottom - rect.top, 0);
      }
    }

    return Math.round(topExtent + bottomExtent);
  }

  setupExitConfirmation() {
    const exitButton = document.getElementById("configurator-exit-button");
    if (!exitButton) return;

    exitButton.addEventListener("click", (event) => {
      event.preventDefault();
      const message =
        t(
          "exit.confirm",
          {},
          "Vous allez quitter le configurateur.\nSi vous n'avez pas ajouté au panier ou copié le lien, vous perdrez cette configuration.\nContinuer ?"
        );
      if (window.confirm(message)) {
        history.back();
      }
    });
  }

  setupProductChangeActions() {
    const changeModelButton = this.dom.elements.changeModelButton;
    if (!changeModelButton || !this.productPickerManager) {
      return;
    }
    const previewTopActions = changeModelButton.closest(".preview-top-actions");

    const refreshAvailability = () => {
      const allProducts =
        typeof window.getSelectableProducts === "function"
          ? window.getSelectableProducts()
          : Array.isArray(window.configuratorData?.selectableProducts)
            ? window.configuratorData.selectableProducts
            : [];
      const currentHandle =
        typeof window.configuratorData?.product?.handle === "string"
          ? window.configuratorData.product.handle.trim()
          : "";
      const hasAlternative = allProducts.some((product) => {
        const handle =
          typeof product?.handle === "string" ? product.handle.trim() : "";
        return Boolean(handle && handle !== currentHandle);
      });
      const isLoading =
        window.configuratorData?.selectableProductsStatus === "loading";
      const modelButtonState = isLoading
        ? "loading"
        : hasAlternative
          ? "visible"
          : "unavailable";

      changeModelButton.disabled = !hasAlternative;
      changeModelButton.setAttribute(
        "aria-disabled",
        (!hasAlternative).toString()
      );
      changeModelButton.tabIndex = hasAlternative ? 0 : -1;
      changeModelButton.setAttribute(
        "aria-hidden",
        (modelButtonState !== "visible").toString()
      );
      previewTopActions?.setAttribute(
        "data-model-button-state",
        modelButtonState
      );
    };

    changeModelButton.addEventListener("click", async (event) => {
      event.preventDefault();
      if (changeModelButton.disabled) {
        return;
      }

      changeModelButton.disabled = true;
      changeModelButton.setAttribute("aria-disabled", "true");

      try {
        await this.productPickerManager.openForManualChange({
          triggerElement: changeModelButton,
        });
      } catch (error) {
        console.error("Unable to open manual model change picker", error);
      } finally {
        refreshAvailability();
      }
    });

    window.addEventListener("configurator:products-updated", refreshAvailability);
    window.addEventListener("configurator:product-updated", refreshAvailability);

    refreshAvailability();
  }

  setupPostCartOfferFlow() {
    window.addEventListener("configurator:cart-add-success", async () => {
      if (!this.productPickerManager || this.productPickerManager.isModalOpen()) {
        return;
      }

      try {
        await this.productPickerManager.openForPostCartOffer();
      } catch (error) {
        console.error("Unable to open post-cart offer picker", error);
      }
    });
  }

  init(url = this.urlManager.requestedURL) {
    if (this.isInitializing && url === this.urlManager.requestedURL) return this.initialization;
    const changedURL = url !== this.urlManager.requestedURL;
    if (this.hasAttemptedInitialization) {
      window.configuratorData.retryFailedRequests();
      this.palettesManager.retryFailedLoad();
    }
    this.hasAttemptedInitialization = true;
    const generation = ++this.generation;
    const isCurrent = () => generation === this.generation;
    this.urlManager.suspend(url);
    this.startup.requestedURL = url;
    this.startup.setStatus("loading");
    this.previewManager.paused = true;
    this.previewManager.patternRenderToken++;
    window.configuratorData.shouldAutoSelectFirst = false;
    if (changedURL) {
      window.configuratorData.invalidateProductSelection();
      this.productPickerManager.closeModal();
      this.productPickerManager.resolveSelection(false);
    }
    this.isInitializing = true;

    this.initialization = (this.initialization || Promise.resolve()).then(async () => {
      if (!isCurrent()) return false;
      try {
        const params = new URL(url).searchParams;
        const data = window.configuratorData;
        if (changedURL && !params.get("product")) {
          data.product = null;
          data.productReadyPromise = Promise.resolve(null);
        }
        await data.ensureProductReady(params.get("product"));
        if (!isCurrent()) return false;
        if (!data.product?.id) {
          await data.ensureSelectableProducts();
          if (!isCurrent()) return false;
          this.startup.setStatus("choosing");
          await this.productPickerManager.requireSelectionIfNeeded();
          if (!isCurrent()) return false;
          this.startup.setStatus("loading");
        }
        await data.ensurePatterns();
        if (!isCurrent()) return false;
        this.designManager.onPatternsReady();
        this.optionsManager.handleOptionsUpdate(data.options);
        this.urlManager.validateOptions(params);
        this.state.updateDesign({ pattern: null, patternColors: {}, paletteLabel: null });
        this.designManager.currentSelectedColors = null;
        this.designManager.clearManualSelection();
        this.urlManager.clearPaletteSelections();

        if (params.has("pattern")) {
          if (!params.get("pattern")) throw this.startup.invalid("pattern");
          await this.urlManager.restoreFromURL(params, isCurrent);
        } else {
          const first = document.querySelector(".pattern-item");
          if (!first) throw this.startup.invalid(data.product?.title || "pattern");
          await this.designManager.selectPattern(first);
        }
        if (!isCurrent()) return false;
        this.previewManager.paused = false;
        await this.previewManager.updatePreview();
        if (!isCurrent()) return false;
        this.startup.setStatus("ready");
        this.dom.hideLoading();
        this.urlManager.resume();
        this.urlManager.updateURL();
        if (this.welcomeHelper?.shouldAutoShow && !this.hasShownWelcome) {
          this.hasShownWelcome = true;
          this.welcomeHelper.open();
        }
        return true;
      } catch (error) {
        if (isCurrent()) {
          this.previewManager.paused = true;
          this.startup.setStatus(error.code === "invalid_configuration" ? "invalid" : "error", error);
          console.error("Unable to initialize configurator", error);
        }
        return false;
      } finally {
        if (isCurrent()) this.isInitializing = false;
      }
    });
    return this.initialization;
  }

  resetConfiguration() {
    const url = new URL(this.urlManager.requestedURL);
    const validProduct = window.configuratorData.product;
    for (const key of [...url.searchParams.keys()]) {
      if (["pattern", "colors", "palette", "paletteId", "product"].includes(key) || key.startsWith("opt_")) {
        url.searchParams.delete(key);
      }
    }
    if (validProduct?.handle && validProduct.handle === new URL(this.urlManager.requestedURL).searchParams.get("product")) {
      url.searchParams.set("product", validProduct.handle);
    }
    window.history.replaceState({}, "", url);
    return this.init(url.href);
  }
}

function initializeConfigurator() {
  if (window.designConfigurator || !document.getElementById("design-configurator")) return;
  try {
    window.designConfigurator = new DesignConfigurator();
    window.designConfigurator.init();
  } catch (error) {
    window.configuratorStartup.setStatus("scripts");
    console.error("Failed to initialize design configurator", error);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeConfigurator, { once: true });
} else {
  initializeConfigurator();
}
